import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMountConfig } from '../mountConfig';
import {
  getLegacyPermissions,
  getLegacyRootScope,
  getLegacyWindow,
  LegacyWindow,
} from './legacyContext';
import { fetchAndResolvePermissions } from './permissionsApi';
import { useAuthStore } from '@/store/authStore';
import type { LegacyPermissions } from '@/types/permissions';

type LegacyPermissionsState = Record<string, unknown>;
type LegacyPermissionsStatus = 'loading' | 'ready';

type LegacyPermissionsContextValue = {
  permissions: LegacyPermissionsState;
  status: LegacyPermissionsStatus;
};

const LegacyPermissionsContext = createContext<LegacyPermissionsContextValue | undefined>(
  undefined
);

function normalizePermissions(perms?: LegacyPermissionsState | null): LegacyPermissionsState {
  if (perms && typeof perms === 'object') {
    return { ...perms };
  }
  return {};
}

function hasLoadedPermissions(perms: LegacyPermissionsState) {
  return Object.keys(perms).length > 0;
}

function subscribeToLegacyPermissions(
  win: LegacyWindow,
  onChange: (next: LegacyPermissionsState) => void
) {
  const root = getLegacyRootScope(win);
  let stopped = false;
  let pollId: number | undefined;

  const pushUpdate = (next?: LegacyPermissionsState) => {
    if (stopped) return false;
    const normalized = normalizePermissions(next);
    onChange(normalized);
    return hasLoadedPermissions(normalized);
  };

  if (root?.$watch) {
    const unwatch = root.$watch(
      'G_PERMISSIONS',
      (next: LegacyPermissionsState) => {
        pushUpdate(next);
      },
      true
    );

    if (typeof unwatch === 'function') {
      pollId = window.setInterval(() => {
        const ready = pushUpdate(getLegacyPermissions(win));
        if (ready && pollId) {
          window.clearInterval(pollId);
          pollId = undefined;
        }
      }, 250);

      return () => {
        stopped = true;
        if (pollId) window.clearInterval(pollId);
        unwatch();
      };
    }
  }

  pollId = window.setInterval(() => {
    const ready = pushUpdate(getLegacyPermissions(win));
    if (ready && pollId) {
      window.clearInterval(pollId);
      pollId = undefined;
    }
  }, 250);

  // Immediate attempt so first render can benefit if permissions already exist.
  pushUpdate(getLegacyPermissions(win));

  return () => {
    stopped = true;
    if (pollId) window.clearInterval(pollId);
  };
}

function shallowEqualPermissions(a: LegacyPermissionsState, b: LegacyPermissionsState) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/**
 * LegacyPermissionsProvider — mode-aware permission bridge.
 *
 * - `standalone`: pass-through. `authStore` is the sole source of permissions
 *   (resolved in `initAuth`); the hooks below read from it directly. No API
 *   call, no state, no context.
 * - `embedded`: subscribes to AngularJS `$rootScope.G_PERMISSIONS` and
 *   optimistically calls the React-side `GetPermissions` for warm-cache
 *   resolution. Whichever returns first populates context.
 */
export function LegacyPermissionsProvider({ children }: { children: ReactNode }) {
  const { mode } = getMountConfig();
  if (mode === 'standalone') {
    return <>{children}</>;
  }
  return <EmbeddedLegacyPermissionsProvider>{children}</EmbeddedLegacyPermissionsProvider>;
}

function EmbeddedLegacyPermissionsProvider({ children }: { children: ReactNode }) {
  const win = getLegacyWindow();
  const mountConfig = getMountConfig();
  const hasMountOverrides =
    Array.isArray(mountConfig.permissionOverrides) && mountConfig.permissionOverrides.length > 0;

  const [state, setState] = useState<LegacyPermissionsContextValue>(() => {
    const initialPermissions = normalizePermissions(getLegacyPermissions(win));
    const initialStatus: LegacyPermissionsStatus =
      hasMountOverrides || hasLoadedPermissions(initialPermissions) ? 'ready' : 'loading';
    return { permissions: initialPermissions, status: initialStatus };
  });

  // Track whether React API already resolved permissions so we don't
  // overwrite them with potentially stale $rootScope data.
  const reactApiResolved = useRef(false);

  useEffect(() => {
    if (hasMountOverrides) {
      return undefined;
    }

    let mounted = true;

    const handleNext = (next: LegacyPermissionsState) => {
      if (!mounted) return;
      setState((prev) => {
        // If React already fetched permissions independently, skip legacy updates
        // unless they contain MORE keys (AngularJS may add extra keys later).
        if (
          reactApiResolved.current &&
          Object.keys(next).length <= Object.keys(prev.permissions).length
        ) {
          return prev;
        }
        const nextStatus: LegacyPermissionsStatus = hasLoadedPermissions(next)
          ? 'ready'
          : prev.status;
        if (shallowEqualPermissions(prev.permissions, next) && prev.status === nextStatus) {
          return prev;
        }
        return { permissions: next, status: nextStatus };
      });
    };

    // VendorConnect has its own auth flow (Auth0); skip the standard
    // GetPermissions call so we don't trigger a 401 redirect during its bootstrap.
    const isVendorConnect =
      (window.location.hash || '').includes('/VendorConnect') ||
      sessionStorage.getItem('vc_session_active') === '1' ||
      sessionStorage.getItem('vc_auth_pending') === 'vendor';

    if (!isVendorConnect) {
      fetchAndResolvePermissions()
        .then((resolved) => {
          if (!mounted) return;
          reactApiResolved.current = true;
          const normalized = normalizePermissions(resolved);
          setState({
            permissions: normalized,
            status: hasLoadedPermissions(normalized) ? 'ready' : 'loading',
          });
          // One-way sync to window.G_PERMISSIONS so any AngularJS code still
          // loaded on the page can read the same permissions object.
          try {
            window.G_PERMISSIONS = normalized;
          } catch {
            // no-op
          }
        })
        .catch(() => {
          // API failure (401, 403, network) is expected for unauthenticated
          // bootstraps. AngularJS's own GetPermissions call (and the
          // $rootScope subscription below) will populate state if the user
          // is actually authenticated.
        });
    }

    const cleanup = subscribeToLegacyPermissions(win, handleNext);
    return () => {
      mounted = false;
      cleanup();
    };
  }, [hasMountOverrides, win]);

  const value = useMemo(() => state, [state]);

  if (value.status !== 'ready') {
    return (
      <LegacyPermissionsContext.Provider value={value}>{null}</LegacyPermissionsContext.Provider>
    );
  }

  return (
    <LegacyPermissionsContext.Provider value={value}>{children}</LegacyPermissionsContext.Provider>
  );
}

/**
 * Read the resolved G_PERMISSIONS-style object.
 *
 * - `standalone`: returns `authStore.permissions` (zustand subscription).
 * - `embedded`:   returns the legacy provider's context value (or, as a final
 *                 fallback for components rendered outside the provider,
 *                 reads directly from `$rootScope.G_PERMISSIONS`).
 */
export function useLegacyPermissions(): LegacyPermissions {
  const { mode } = getMountConfig();
  const authStorePermissions = useAuthStore((s) => s.permissions);
  const context = useContext(LegacyPermissionsContext);

  if (mode === 'standalone') {
    return authStorePermissions as LegacyPermissions;
  }

  if (context) {
    return context.permissions as LegacyPermissions;
  }

  return normalizePermissions(getLegacyPermissions()) as LegacyPermissions;
}

/**
 * Status of the permission resolution.
 *
 * - `standalone`: derived from `authStore.status` — `'authenticated'` maps to
 *   `'ready'`, every other state is treated as `'loading'`. AuthGate handles
 *   non-authenticated states elsewhere, so callers of this hook only ever see
 *   `'ready'` when they should render permission-gated UI.
 * - `embedded`:   reads from the legacy provider's context.
 */
export function useLegacyPermissionStatus(): LegacyPermissionsStatus {
  const { mode } = getMountConfig();
  const authStatus = useAuthStore((s) => s.status);
  const context = useContext(LegacyPermissionsContext);

  if (mode === 'standalone') {
    return authStatus === 'authenticated' ? 'ready' : 'loading';
  }

  if (context) {
    return context.status;
  }

  return hasLoadedPermissions(normalizePermissions(getLegacyPermissions())) ? 'ready' : 'loading';
}
